import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaSync } from 'react-icons/fa';
import WizardFooterSlot from 'common/components/wizardFooterSlot/WizardFooterSlot';
import TablePagination from 'common/components/tablePagination/TablePagination';
import { FamilyDto } from 'api/families/models/FamilyDto';
import { SelectInputOption } from 'common/components/selectInput/SelectInput';
import { ExcelSheetData, ElementColumnMappingEntry, ElementImportRow, MappingFieldOption } from '../utils/types';
import { cleanElementName } from '../utils/familyMatcher';
import styles from './StepMapping.module.scss';

const ROWS_PER_PAGE = 100;

interface Props {
    excelData: ExcelSheetData;
    headerRowNumber: number;
    columnMappings: ElementColumnMappingEntry[];
    importRows: ElementImportRow[];
    families: FamilyDto[];
    mappingFields: MappingFieldOption[];
    onColumnMappingChange: (colIndex: number, field: string | null) => void;
    onRowFamilyChange: (rowIndex: number, familyId: string | null) => void;
    onRowSubFamilyChange: (rowIndex: number, subFamilyId: string | null) => void;
    onCellEdit: (rowIndex: number, colIndex: number, value: string) => void;
    onHeaderRowChange: (row: number) => void;
    onRefreshFamilies: () => void;
    elementTypes: SelectInputOption[];
    workTypes: SelectInputOption[];
    onRowElementTypeChange: (rowIndex: number, elementTypeId: string | null) => void;
    onRowWorkTypeChange: (rowIndex: number, workTypeId: string | null) => void;
    onRefreshCatalogs: () => void;
}

const StepMapping: React.FC<Props> = ({
    excelData, headerRowNumber, columnMappings, importRows, families,
    mappingFields,
    onColumnMappingChange, onRowFamilyChange, onRowSubFamilyChange, onCellEdit, onHeaderRowChange, onRefreshFamilies,
    elementTypes, workTypes, onRowElementTypeChange, onRowWorkTypeChange, onRefreshCatalogs,
}) => {
    const { t } = useTranslation();
    const [editingCell, setEditingCell] = useState<{ rowIndex: number; colIndex: number } | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [showOnlyUnmapped, setShowOnlyUnmapped] = useState(false);

    const allRows = excelData.rows;
    const headerRowIdx = headerRowNumber - 1;

    // Column mapping exclusivity
    const usedFields = new Set(
        columnMappings.filter(m => m.elementField).map(m => m.elementField)
    );

    const getAvailableFields = (currentField: string | null) => {
        return mappingFields.filter(f => f.value === currentField || !usedFields.has(f.value));
    };

    // Find columns that have data in at least one row (across all rows)
    const visibleColIndices = useMemo(() => {
        const maxCols = Math.max(...allRows.map(r => r.length), 0);
        return Array.from({ length: maxCols }, (_, idx) => idx)
            .filter(colIdx => allRows.some(row => row[colIdx]?.trim()));
    }, [allRows]);

    // Filter out completely empty rows, but always keep the header row
    const visibleRows = useMemo(() => {
        return allRows
            .map((row, idx) => ({ row, rowIdx: idx }))
            .filter(({ row, rowIdx }) =>
                rowIdx === headerRowIdx || row.some(cell => cell?.trim())
            );
    }, [allRows, headerRowIdx]);

    // Build a map from data row index to ImportRow for quick lookup
    const importRowMap = useMemo(() => {
        const map = new Map<number, ElementImportRow>();
        importRows.forEach(r => map.set(r.rowIndex, r));
        return map;
    }, [importRows]);

    // Apply unmapped filter
    const filteredRows = useMemo(() => {
        if (!showOnlyUnmapped) return visibleRows;
        return visibleRows.filter(({ rowIdx }) => {
            if (rowIdx === headerRowIdx) return true;
            if (rowIdx < headerRowIdx) return false;
            const dataRowIndex = rowIdx - headerRowNumber;
            const importRow = importRowMap.get(dataRowIndex);
            if (!importRow) return false;
            const hasData = importRow.rawCells.some(c => c?.trim());
            return hasData && !importRow.familyId;
        });
    }, [visibleRows, showOnlyUnmapped, headerRowIdx, headerRowNumber, importRowMap]);

    const totalPages = Math.ceil(filteredRows.length / ROWS_PER_PAGE);

    // Paginated rows
    const paginatedRows = useMemo(() => {
        const start = currentPage * ROWS_PER_PAGE;
        return filteredRows.slice(start, start + ROWS_PER_PAGE);
    }, [filteredRows, currentPage]);

    // Count unmapped rows (no family, with data)
    const unmappedCount = importRows.filter(r => !r.familyId && r.rawCells.some(c => c?.trim())).length;

    const getSubFamilies = (familyId: string | null): FamilyDto[] => {
        if (!familyId) return [];
        const family = families.find(f => f.id === familyId);
        return family?.subFamilies ?? [];
    };

    const getCellValue = (row: ElementImportRow, colIndex: number, rowIdx: number): string => {
        if (row.editedCells && row.editedCells[colIndex] !== undefined) {
            return row.editedCells[colIndex];
        }
        const mapping = columnMappings.find(m => m.excelColumnIndex === colIndex);
        // Use formatted value for name (strip price suffix)
        if (mapping?.elementField === 'name') {
            const formatted = allRows[rowIdx]?.[colIndex] ?? '';
            return cleanElementName(formatted);
        }
        return row.rawCells[colIndex] ?? '';
    };

    const handleCellClick = (rowIndex: number, colIndex: number) => {
        setEditingCell({ rowIndex, colIndex });
    };

    const handleCellBlur = (row: ElementImportRow, colIndex: number, value: string) => {
        const originalValue = row.rawCells[colIndex] ?? '';
        if (value !== originalValue) {
            onCellEdit(row.rowIndex, colIndex, value);
        }
        setEditingCell(null);
    };

    const hasMappings = columnMappings.some(m => m.elementField) || importRows.some(r => r.editedCells);

    const handleHeaderRowChange = (rowIdx: number) => {
        if (hasMappings) {
            if (!window.confirm(t('elements.import_wizard.confirm_header_change'))) {
                return;
            }
        }
        onHeaderRowChange(rowIdx + 1);
        // Auto-navigate to page containing the new header row
        const visibleIndex = visibleRows.findIndex(r => r.rowIdx === rowIdx);
        const targetPage = visibleIndex >= 0 ? Math.floor(visibleIndex / ROWS_PER_PAGE) : 0;
        if (targetPage !== currentPage) {
            setCurrentPage(targetPage);
        }
    };

    // Column resize logic
    // Column keys: 'radio', 'family', 'subFamily', 'elementType', 'workType', ...excelColIndices
    const defaultWidths = useMemo(() => {
        const widths: Record<string, number> = {
            radio: 36,
            family: 180,
            subFamily: 180,
            elementType: 180,
            workType: 180,
        };
        for (const colIdx of visibleColIndices) {
            widths[`col_${colIdx}`] = 200;
        }
        return widths;
    }, [visibleColIndices]);

    const [colWidths, setColWidths] = useState<Record<string, number>>({});

    // Merge defaults with user-set widths
    const getWidth = (key: string) => colWidths[key] ?? defaultWidths[key] ?? 200;

    // Computed sticky left positions
    const radioLeft = 0;
    const familyLeft = getWidth('radio');
    const subFamilyLeft = familyLeft + getWidth('family');

    const handleResizeStart = useCallback((colKey: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startWidth = getWidth(colKey);

        const handleMouseMove = (ev: MouseEvent) => {
            const diff = ev.clientX - startX;
            const newWidth = Math.max(50, startWidth + diff);
            setColWidths(prev => ({ ...prev, [colKey]: newWidth }));
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, [colWidths, defaultWidths]);

    const resizeHandle = (colKey: string) => (
        <div
            className={styles.resizeHandle}
            onMouseDown={(e) => handleResizeStart(colKey, e)}
        />
    );

    return (
        <div className={styles.container}>
            <div className={styles.toolbar}>
                <div className={styles.description}>
                    <p>{t('elements.import_wizard.step_mapping_description')}</p>
                </div>
                <label className={styles.filterToggle}>
                    <input
                        type="checkbox"
                        checked={showOnlyUnmapped}
                        onChange={(e) => {
                            setShowOnlyUnmapped(e.target.checked);
                            setCurrentPage(0);
                        }}
                    />
                    <span>{t('elements.import_wizard.show_only_unmapped', { count: unmappedCount })}</span>
                </label>
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.table} style={{ width: getWidth('radio') + getWidth('family') + getWidth('subFamily') + getWidth('elementType') + getWidth('workType') + visibleColIndices.reduce((sum, idx) => sum + getWidth(`col_${idx}`), 0) }}>
                    <colgroup>
                        <col style={{ width: getWidth('radio') }} />
                        <col style={{ width: getWidth('family') }} />
                        <col style={{ width: getWidth('subFamily') }} />
                        <col style={{ width: getWidth('elementType') }} />
                        <col style={{ width: getWidth('workType') }} />
                        {visibleColIndices.map(colIdx => (
                            <col key={colIdx} style={{ width: getWidth(`col_${colIdx}`) }} />
                        ))}
                    </colgroup>
                    <thead>
                        {/* Mapping row - sticky at top */}
                        <tr className={styles.mappingRow}>
                            <th className={styles.radioCol} style={{ left: radioLeft }}></th>
                            <th className={styles.familyCol} style={{ left: familyLeft }}>
                                <div className={styles.colHeader}>
                                    <span>{t('elements.import_wizard.field_family')}</span>
                                    <div className={styles.colHeaderActions}>
                                        <button
                                            className={styles.colHeaderBtn}
                                            title={t('elements.import_wizard.create_family')}
                                            onClick={() => window.open('/library/elements/families/create', '_blank')}
                                        >
                                            <FaPlus />
                                        </button>
                                        <button
                                            className={styles.colHeaderBtn}
                                            title={t('elements.import_wizard.refresh_families')}
                                            onClick={onRefreshFamilies}
                                        >
                                            <FaSync />
                                        </button>
                                    </div>
                                </div>
                                {resizeHandle('family')}
                            </th>
                            <th className={styles.subFamilyCol} style={{ left: subFamilyLeft }}>
                                <div className={styles.colHeader}>
                                    <span>{t('elements.import_wizard.field_sub_family')}</span>
                                    <div className={styles.colHeaderActions}>
                                        <button
                                            className={styles.colHeaderBtn}
                                            title={t('elements.import_wizard.create_sub_family')}
                                            onClick={() => window.open('/library/elements/sub-families/create', '_blank')}
                                        >
                                            <FaPlus />
                                        </button>
                                        <button
                                            className={styles.colHeaderBtn}
                                            title={t('elements.import_wizard.refresh_families')}
                                            onClick={onRefreshFamilies}
                                        >
                                            <FaSync />
                                        </button>
                                    </div>
                                </div>
                                {resizeHandle('subFamily')}
                            </th>
                            <th className={styles.extraCol}>
                                <div className={styles.colHeader}>
                                    <span>{t('elements.import_wizard.element_type')}</span>
                                    <div className={styles.colHeaderActions}>
                                        <button
                                            className={styles.colHeaderBtn}
                                            title={t('elements.import_wizard.create_element_type')}
                                            onClick={() => window.open('/library/element-types/create', '_blank')}
                                        >
                                            <FaPlus />
                                        </button>
                                        <button
                                            className={styles.colHeaderBtn}
                                            title={t('elements.import_wizard.refresh_families')}
                                            onClick={onRefreshCatalogs}
                                        >
                                            <FaSync />
                                        </button>
                                    </div>
                                </div>
                                {resizeHandle('elementType')}
                            </th>
                            <th className={styles.extraCol}>
                                <div className={styles.colHeader}>
                                    <span>{t('elements.import_wizard.work_type')}</span>
                                    <div className={styles.colHeaderActions}>
                                        <button
                                            className={styles.colHeaderBtn}
                                            title={t('elements.import_wizard.create_work_type')}
                                            onClick={() => window.open('/library/work-types/create', '_blank')}
                                        >
                                            <FaPlus />
                                        </button>
                                        <button
                                            className={styles.colHeaderBtn}
                                            title={t('elements.import_wizard.refresh_families')}
                                            onClick={onRefreshCatalogs}
                                        >
                                            <FaSync />
                                        </button>
                                    </div>
                                </div>
                                {resizeHandle('workType')}
                            </th>
                            {visibleColIndices.map(colIndex => {
                                const mapping = columnMappings.find(m => m.excelColumnIndex === colIndex);
                                const currentField = mapping?.elementField ?? null;
                                const availableFields = getAvailableFields(currentField);

                                return (
                                    <th key={colIndex} className={styles.mappingCell}>
                                        <select
                                            value={currentField ?? ''}
                                            onChange={(e) => onColumnMappingChange(
                                                colIndex,
                                                e.target.value || null
                                            )}
                                            className={styles.mappingSelect}
                                        >
                                            <option value="">{t('elements.import_wizard.field_not_mapped')}</option>
                                            {availableFields.map(f => (
                                                <option key={f.value} value={f.value}>{f.label}</option>
                                            ))}
                                        </select>
                                        {resizeHandle(`col_${colIndex}`)}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedRows.map(({ row, rowIdx }) => {
                            const isHeader = rowIdx === headerRowIdx;
                            const isAboveHeader = rowIdx < headerRowIdx;
                            const dataRowIndex = rowIdx - headerRowNumber;
                            const importRow = !isHeader && !isAboveHeader ? importRowMap.get(dataRowIndex) : null;
                            const isDataRow = !isHeader && !isAboveHeader && importRow;

                            const hasData = importRow?.rawCells.some(c => c?.trim());
                            const isWarning = isDataRow && !importRow.familyId && hasData;
                            const subFamilies = isDataRow ? getSubFamilies(importRow.familyId) : [];

                            const rowClassName = [
                                styles.dataRow,
                                isHeader ? styles.headerSelectedRow : '',
                                isAboveHeader ? styles.aboveHeaderRow : '',
                                isWarning ? styles.warningRow : '',
                            ].filter(Boolean).join(' ');

                            return (
                                <tr key={rowIdx} className={rowClassName}>
                                    <td className={styles.radioCol} style={{ left: radioLeft }}>
                                        <input
                                            type="radio"
                                            name="headerRow"
                                            checked={isHeader}
                                            onChange={() => handleHeaderRowChange(rowIdx)}
                                            className={styles.radioInput}
                                        />
                                    </td>
                                    <td className={styles.familyCol} style={{ left: familyLeft }}>
                                        {isDataRow && (
                                            <select
                                                value={importRow.familyId ?? ''}
                                                onChange={(e) => onRowFamilyChange(
                                                    importRow.rowIndex,
                                                    e.target.value || null
                                                )}
                                                className={`${styles.familySelect} ${isWarning ? styles.familyWarning : ''}`}
                                            >
                                                <option value="">{t('elements.import_wizard.field_not_mapped')}</option>
                                                {families.map(f => (
                                                    <option key={f.id} value={f.id}>{f.name}</option>
                                                ))}
                                            </select>
                                        )}
                                    </td>
                                    <td className={styles.subFamilyCol} style={{ left: subFamilyLeft }}>
                                        {isDataRow && (
                                            <select
                                                value={importRow.subFamilyId ?? ''}
                                                onChange={(e) => onRowSubFamilyChange(
                                                    importRow.rowIndex,
                                                    e.target.value || null
                                                )}
                                                className={styles.subFamilySelect}
                                                disabled={!importRow.familyId || subFamilies.length === 0}
                                            >
                                                <option value="">{'-'}</option>
                                                {subFamilies.map(sf => (
                                                    <option key={sf.id} value={sf.id}>{sf.name}</option>
                                                ))}
                                            </select>
                                        )}
                                    </td>
                                    <td className={styles.extraCol}>
                                        {isDataRow && (
                                            <select
                                                value={importRow.elementTypeId ?? ''}
                                                onChange={(e) => onRowElementTypeChange(importRow.rowIndex, e.target.value || null)}
                                                className={styles.extraSelect}
                                            >
                                                <option value="">{'-'}</option>
                                                {elementTypes.map(et => (
                                                    <option key={et.value} value={et.value}>{et.label}</option>
                                                ))}
                                            </select>
                                        )}
                                    </td>
                                    <td className={styles.extraCol}>
                                        {isDataRow && (
                                            <select
                                                value={importRow.workTypeId ?? ''}
                                                onChange={(e) => onRowWorkTypeChange(importRow.rowIndex, e.target.value || null)}
                                                className={styles.extraSelect}
                                            >
                                                <option value="">{'-'}</option>
                                                {workTypes.map(wt => (
                                                    <option key={wt.value} value={wt.value}>{wt.label}</option>
                                                ))}
                                            </select>
                                        )}
                                    </td>
                                    {visibleColIndices.map(colIndex => {
                                        if (!isDataRow) {
                                            return (
                                                <td key={colIndex} className={isHeader ? styles.headerCell : styles.dataCell} title={row[colIndex]}>
                                                    {row[colIndex]}
                                                </td>
                                            );
                                        }

                                        const isEditing = editingCell?.rowIndex === importRow.rowIndex && editingCell?.colIndex === colIndex;
                                        const cellValue = getCellValue(importRow, colIndex, rowIdx);
                                        const isEdited = importRow.editedCells?.[colIndex] !== undefined;

                                        return (
                                            <td
                                                key={colIndex}
                                                className={`${styles.dataCell} ${isEdited ? styles.editedCell : ''}`}
                                                title={cellValue}
                                                onClick={() => !isEditing && handleCellClick(importRow.rowIndex, colIndex)}
                                            >
                                                {isEditing
                                                    ? (
                                                        <input
                                                            type="text"
                                                            defaultValue={cellValue}
                                                            className={styles.cellInput}
                                                            autoFocus
                                                            onBlur={(e) => handleCellBlur(importRow, colIndex, e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    (e.target as HTMLInputElement).blur();
                                                                }
                                                                if (e.key === 'Escape') {
                                                                    setEditingCell(null);
                                                                }
                                                            }}
                                                        />
                                                    )
                                                    : cellValue
                                                }
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <WizardFooterSlot>
                    <TablePagination
                        page={currentPage}
                        totalPages={totalPages}
                        onChange={setCurrentPage}
                        previousLabel={t('elements.import_wizard.previous')}
                        nextLabel={t('elements.import_wizard.next')}
                        info={t('elements.import_wizard.page_info', { page: currentPage + 1, total: totalPages })}
                        firstTitle={t('elements.import_wizard.first_page')}
                        lastTitle={t('elements.import_wizard.last_page')}
                    />
                </WizardFooterSlot>
            )}

            {unmappedCount > 0 && (
                <div className={styles.warning}>
                    {t('elements.import_wizard.unmapped_rows_warning', { count: unmappedCount })}
                </div>
            )}
        </div>
    );
};

export default StepMapping;
